import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { app_permissions } from '../security/app-permissions';
import { PermissionService } from '../security/permission.service';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../shared/page-layout/page-detail-hero.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { FieldLaborRequirement, FieldWorkPackage, FieldWorkTargetChange, FieldWorkTargetType } from './field-work.models';
import { FieldWorkExecutionDialogComponent } from './field-work-execution-dialog.component';
import { FieldWorkLaborPlanningDialogComponent } from './field-work-labor-planning-dialog.component';
import { FieldWorkService } from './field-work.service';

@Component({
  selector: 'app-field-work-package-detail',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink, ButtonModule, DatePickerModule, DialogModule, InputNumberModule,
    InputTextModule, ProgressBarModule, SelectModule, TableModule, TagModule, TabsModule, BadgeModule,
    PageShellComponent, PageDetailHeroComponent, PageMetricStripComponent, PageDataPanelComponent,
    FieldWorkLaborPlanningDialogComponent, FieldWorkExecutionDialogComponent
  ],
  templateUrl: './field-work-package-detail.component.html',
  styleUrl: './field-work-package-detail.component.scss'
})
export class FieldWorkPackageDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(FieldWorkService);
  private readonly permissions = inject(PermissionService);
  private readonly router = inject(Router);

  workPackage?: FieldWorkPackage;
  loading = true;
  error = '';
  activeTab = 'overview';
  readonly planningVisible = signal(false);
  readonly executionVisible = signal(false);
  readonly packageActionVisible = signal(false);
  readonly packageActionSaving = signal(false);
  packageActionMode: 'REPLAN' | 'TRANSFER' | 'SPLIT' = 'REPLAN';
  packageActionReason = '';
  packageActionTargetType?: FieldWorkTargetType;
  packageActionTargetId?: number;
  packageActionTitle = '';
  packageActionStart?: Date;
  packageActionEnd?: Date;
  readonly targetTypeOptions: FieldWorkTargetType[] = [
    'SHIPMENT', 'SHIPMENT_DELIVERY', 'TRANSPORT_ORDER_RELEASE', 'POSM_DELIVERY_SCHEDULE',
    'POSM_INSTALLATION_TASK', 'POSM_REMOVAL_TASK', 'CONTAINER_CASE', 'CONTAINER_JOURNEY_ITEM',
    'WAREHOUSE', 'WAREHOUSE_TRANSFER', 'WAREHOUSE_DISPATCH', 'CUSTOMS_CLEARANCE_HEADER', 'LOGISTICS_PROJECT'
  ];

  readonly canManage = this.permissions.hasPermission(app_permissions.operations.fieldWork.manage);
  readonly canAssign = this.permissions.hasPermission(app_permissions.operations.fieldWork.assign);
  readonly canExecute = this.permissions.hasPermission(app_permissions.operations.fieldWork.execute);
  readonly canApprove = this.permissions.hasPermission(app_permissions.operations.fieldWork.approve);
  readonly canOverride = this.permissions.hasPermission(app_permissions.operations.fieldWork.override);
  readonly canViewEquipment = this.permissions.hasPermission(app_permissions.operations.equipment.view);
  readonly canManageEquipment = this.permissions.hasPermission(app_permissions.operations.equipment.manage);
  readonly canCheckout = this.permissions.hasPermission(app_permissions.operations.equipment.checkout);
  readonly canViewLaborRates = this.permissions.hasAnyPermission([
    app_permissions.accounts.workforce_finance.view,
    app_permissions.accounts.workforce_finance.rates_manage,
    app_permissions.operations.fieldWork.financeView
  ]);
  readonly canViewEquipmentRates = this.permissions.hasAnyPermission([
    app_permissions.operations.equipment.ratesView,
    app_permissions.operations.equipment.ratesManage,
    app_permissions.operations.fieldWork.financeView
  ]);
  readonly canViewRates = this.permissions.hasAnyPermission([
    app_permissions.accounts.workforce_finance.view,
    app_permissions.operations.equipment.ratesView,
    app_permissions.operations.fieldWork.financeView
  ]);

  get canRelease(): boolean {
    return this.permissions.hasPermission(app_permissions.operations.fieldWork.release);
  }

  get canPlanResources(): boolean {
    return ['DRAFT', 'PLANNED'].includes(this.workPackage?.status || 'DRAFT')
      && (this.canManage || this.canAssign || this.canRelease || this.canManageEquipment);
  }

  get planningActionLabel(): string {
    return this.workPackage?.assignmentAllowed ? 'Plan & assign resources' : 'Gather requirements';
  }

  get canOpenExecution(): boolean {
    return ['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(this.workPackage?.status || '')
      && (this.canExecute || this.canApprove || this.canCheckout || this.canRelease);
  }

  get canChangePackage(): boolean {
    return this.canManage && !['COMPLETED', 'CANCELLED'].includes(this.workPackage?.status || '');
  }

  get metrics(): PageMetric[] {
    const work = this.workPackage;
    return [
      { label: 'Labor coverage', value: `${work?.laborAssignments?.length || 0} / ${this.requiredLabor()}`, tone: 'info' },
      { label: 'Equipment coverage', value: `${this.allocatedEquipment()} / ${this.requiredEquipment()}`, tone: 'info' },
      { label: 'Planning blockers', value: work?.blockers?.length || 0,
        tone: (work?.blockers?.length || 0) > 0 ? 'danger' : 'success' },
      { label: 'Planning warnings', value: work?.warnings?.length || 0,
        tone: (work?.warnings?.length || 0) > 0 ? 'warn' : 'default' }
    ];
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id) || id <= 0) {
      this.error = 'Invalid field work package.';
      this.loading = false;
      return;
    }
    this.loading = true;
    this.service.packageById(id).subscribe({
      next: value => { this.workPackage = value; this.loading = false; },
      error: error => {
        this.error = error?.error?.message || error?.message || 'Field work package could not be loaded.';
        this.loading = false;
      }
    });
  }

  openPlanning(): void {
    this.planningVisible.set(true);
  }

  onTabChange(value: string | number): void {
    this.activeTab = String(value);
  }

  openExecution(): void {
    this.executionVisible.set(true);
  }

  openPackageAction(): void {
    const work = this.workPackage;
    if (!work) return;
    this.packageActionMode = 'REPLAN';
    this.packageActionReason = '';
    this.packageActionTargetType = work.targetType;
    this.packageActionTargetId = work.targetId;
    this.packageActionTitle = work.title || '';
    this.packageActionStart = this.toDate(work.plannedStart);
    this.packageActionEnd = this.toDate(work.plannedEnd);
    this.packageActionVisible.set(true);
  }

  savePackageAction(): void {
    const work = this.workPackage;
    if (!work?.fieldWorkPackageId || !this.packageActionReason.trim()) {
      this.error = 'An audited reason is required.';
      return;
    }
    if (!this.packageActionStart || !this.packageActionEnd || this.packageActionEnd <= this.packageActionStart) {
      this.error = 'The planned end must be after the planned start.';
      return;
    }
    this.packageActionSaving.set(true);
    this.error = '';
    if (this.packageActionMode === 'REPLAN') {
      this.service.savePackage({
        ...work,
        title: this.packageActionTitle.trim() || work.title,
        plannedStart: this.packageActionStart,
        plannedEnd: this.packageActionEnd,
        replanReason: this.packageActionReason.trim()
      }).pipe(finalize(() => this.packageActionSaving.set(false))).subscribe({
        next: value => this.finishPackageAction(value),
        error: error => this.error = this.errorMessage(error)
      });
      return;
    }
    if (!this.canOverride || !this.packageActionTargetType || !this.packageActionTargetId) {
      this.packageActionSaving.set(false);
      this.error = 'Transfer and split require override permission and a destination target.';
      return;
    }
    const change: FieldWorkTargetChange = {
      targetType: this.packageActionTargetType,
      targetId: this.packageActionTargetId,
      reason: this.packageActionReason.trim(),
      title: this.packageActionTitle.trim() || undefined,
      plannedStart: this.packageActionStart,
      plannedEnd: this.packageActionEnd
    };
    const request = this.packageActionMode === 'TRANSFER'
      ? this.service.transferPackage(work.fieldWorkPackageId, change)
      : this.service.splitPackage(work.fieldWorkPackageId, change);
    request.pipe(finalize(() => this.packageActionSaving.set(false))).subscribe({
      next: value => this.finishPackageAction(value),
      error: error => this.error = this.errorMessage(error)
    });
  }

  onPackageChanged(value: FieldWorkPackage): void {
    this.workPackage = value;
  }

  private finishPackageAction(value: FieldWorkPackage): void {
    this.packageActionVisible.set(false);
    if (this.packageActionMode === 'SPLIT' && value.fieldWorkPackageId !== this.workPackage?.fieldWorkPackageId) {
      this.router.navigate(['/operations/field-work/work-packages', value.fieldWorkPackageId]);
      return;
    }
    this.workPackage = value;
  }

  private toDate(value?: string | Date): Date | undefined {
    if (!value) return undefined;
    const parsed = value instanceof Date ? new Date(value) : new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message || 'Field work package could not be changed.';
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(status || '')) return 'success';
    if (['DRAFT', 'PLANNED'].includes(status || '')) return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'info';
  }

  requiredLabor(): number {
    return (this.workPackage?.laborRequirements || []).reduce((sum, row) => sum + (row.headcount || 0), 0);
  }

  assignedTo(requirement: FieldLaborRequirement): number {
    return (this.workPackage?.laborAssignments || [])
      .filter(row => row.fieldLaborRequirementId === requirement.fieldLaborRequirementId).length;
  }

  requiredEquipment(): number {
    return (this.workPackage?.equipmentRequirements || []).reduce((sum, row) => sum + (row.requiredQuantity || 0), 0);
  }

  allocatedEquipment(): number {
    return (this.workPackage?.equipmentAllocations || []).reduce((sum, row) => sum + (row.plannedQuantity || 0), 0);
  }

  targetRoute(): any[] | null {
    const work = this.workPackage;
    if (!work?.targetId) return null;
    const routes: Partial<Record<FieldWorkTargetType, any[]>> = {
      SHIPMENT: ['/shipment/view', work.targetId],
      SHIPMENT_DELIVERY: ['/shipment/delivery/view', work.targetId],
      POSM_DELIVERY_SCHEDULE: ['/posm/executions/delivery-schedules', work.targetId],
      CONTAINER_CASE: ['/container-tracking/cases/view', work.targetId],
      WAREHOUSE_TRANSFER: ['/warehousing/transfers/view', work.targetId],
      CUSTOMS_CLEARANCE_HEADER: ['/clearance/view', work.targetId],
      LOGISTICS_PROJECT: ['/logistics-projects', work.targetId]
    };
    return work.targetType ? routes[work.targetType] || null : null;
  }
}
